import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiCall } from '../hooks/useApiCall';
import { checkVKIDCompatibility, isMobileDevice, isStandaloneApp } from '../utils/mobileUtils';
import { getServiceHeaders } from '../utils/serviceUtils';
import { sessionGet, sessionRemove, sessionSet, storageGet, storageRemove, storageSet } from '../utils/storage';
import { authLogger } from '../utils/logger';
import { ConnectionTokenData } from '../types';
import { API_BASE } from '../utils/api';
import * as VKID from '@vkid/sdk';

interface VKConnectProps {
  appId: string;
  /**
   * ssmm-token аккаунта, К КОТОРОМУ подключаем (главный аккаунт связывания).
   * Ручка connect требует его в заголовке: без токена шлюз отвечает
   * 2002 UNAUTHORIZED «Не передан токен», и связывание падало ещё до
   * подтверждения — выглядело как «SSO снова просит войти».
   */
  sessionToken: string | null;
  onSuccess: (connectionToken: string, connectionData: ConnectionTokenData) => void;
  onCancel: () => void;
}

type VKSilentPayload = {
  token?: string;
  uuid?: string;
  code?: string;
  device_id?: string;
  type?: string;
  state?: string;
  payload?: {
    token?: string;
    uuid?: string;
    code?: string;
    device_id?: string;
    type?: string;
    state?: string;
  };
};

const parseSilentPayload = (payload: VKSilentPayload) => ({
  token: payload?.token ?? payload?.payload?.token,
  uuid: payload?.uuid ?? payload?.payload?.uuid,
  code: payload?.code ?? payload?.payload?.code,
  deviceId: payload?.device_id ?? payload?.payload?.device_id,
  type: payload?.type ?? payload?.payload?.type,
  state: payload?.state ?? payload?.payload?.state,
});

const encodeState = (value: unknown): string => {
  return btoa(JSON.stringify(value))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const normalizeState = (value?: string | null): string | null => {
  if (!value) return null;
  return value
    .trim()
    .replace(/ /g, '+')
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .replace(/=+$/g, '');
};

const VKConnect: React.FC<VKConnectProps> = ({ appId, sessionToken, onSuccess, onCancel }) => {
  const { setLoading, setError } = useAuth();
  const { execute } = useApiCall();
  const [isVKIDLoaded, setIsVKIDLoaded] = useState(false);
  const [isVKIDReady, setIsVKIDReady] = useState(false);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);
  const oneTapRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const successProcessedRef = useRef(false);
  const callbackProcessedRef = useRef(false);

  const cleanupTempStorage = useCallback(() => {
    // PKCE-артефакты держим в localStorage (см. ниже про iOS), чистим оба хранилища.
    storageRemove('vkid_connect_state');
    storageRemove('vkid_connect_code_verifier');
    sessionRemove('vkid_connect_state');
    sessionRemove('vkid_connect_code_verifier');
  }, []);

  // Генерируем случайную строку для state (защита от CSRF)
  const generateRandomString = (length: number = 32) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Генерируем codeVerifier для PKCE
  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  useEffect(() => {
    const compatibility = checkVKIDCompatibility();
    if (!compatibility.compatible) {
      console.warn('VK ID Compatibility Issues:', compatibility.issues);
      setError(`Проблемы совместимости: ${compatibility.issues.join('; ')}`);
    }
    
    setIsVKIDLoaded(true);
  }, []);

  useEffect(() => {
    if (isVKIDLoaded && appId && containerRef.current && !isProcessingCallback) {
      const currentParams = new URLSearchParams(window.location.search);
      const hasCallbackParams = Boolean(currentParams.get('state')) && (
        Boolean(currentParams.get('payload'))
        || (Boolean(currentParams.get('code')) && Boolean(currentParams.get('device_id')))
      );

      if (hasCallbackParams) {
        return;
      }

      try {
        console.log('Initializing VK ID SDK for Connect with appId:', appId);
        setIsVKIDReady(false);

        const parsedAppId = Number(appId);
        if (!Number.isFinite(parsedAppId)) {
          throw new Error('Invalid VK App ID');
        }
        
        const randomState = generateRandomString();
        const state = encodeState({ random: randomState, mode: 'connect' });
        const codeVerifier = generateCodeVerifier();
        
        // localStorage, не sessionStorage: на мобиле connect-флоу идёт через top-level
        // редирект на id.vk.ru и обратно, а iOS Safari ненадёжно переносит sessionStorage
        // через кросс-origin раунд-трип — терялся vkid_connect_state и связывание тихо падало.
        // Приватный режим Safari не даёт писать в localStorage — подстраховываемся sessionStorage.
        if (!(storageSet('vkid_connect_state', state) && storageSet('vkid_connect_code_verifier', codeVerifier))) {
          sessionSet('vkid_connect_state', state);
          sessionSet('vkid_connect_code_verifier', codeVerifier);
        }

        // В popup/iframe важно оставаться в текущем окне. В standalone-PWA и на мобильных
        // InNewTab открывает внешний браузер (на iOS — Safari) и теряет контекст PWA,
        // поэтому callback не возвращается — используем Redirect в том же окне.
        const hasParent = Boolean(window.opener) || window.parent !== window;
        const shouldUseRedirect = hasParent || isStandaloneApp() || isMobileDevice();

        const configData = {
          app: parsedAppId,
          redirectUrl: window.location.origin,
          state: state,
          codeVerifier: codeVerifier,
          scope: 'email phone',
          mode: shouldUseRedirect
            ? VKID.ConfigAuthMode.Redirect
            : VKID.ConfigAuthMode.InNewTab,
        };
        
        VKID.Config.init(configData);

        const oneTap = new VKID.OneTap();
        oneTapRef.current = oneTap;

        oneTap
          .on(VKID.WidgetEvents.ERROR, (error: any) => {
            console.error('VK ID Widget Error:', error);
            handleVKIDError(error);
          })
          .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload: any) => {
            console.log('VK ID LOGIN_SUCCESS event received for connect:', payload);
            handleVKIDConnectSuccess(payload);
          })
          .on(VKID.WidgetEvents.START_LOAD, () => {
            // Шаблон кнопки уже отрендерен — снимаем "Инициализация...".
            // LOAD ждать не стоит: он стреляет только когда iframe fast-auth догрузится
            // с `id.vk.ru` и у пользователей без активной VK сессии может не выстрелить.
            console.log('VK ID Widget started loading, button is ready');
            setIsVKIDReady(true);
          })
          .on(VKID.WidgetEvents.LOAD, () => {
            console.log('VK ID Widget fully loaded (fast-auth iframe ready)');
            setIsVKIDReady(true);
          });

        containerRef.current.innerHTML = '';
        oneTap.render({
          container: containerRef.current,
          skin: VKID.OneTapSkin.Primary,
          scheme: VKID.Scheme.LIGHT,
          lang: VKID.Languages.RUS,
          styles: {
            height: 44,
            borderRadius: 12,
          }
        });

        // Шаблон кнопки вставлен синхронно — она уже кликабельна, не зависим от iframe.
        setIsVKIDReady(true);

        console.log('VK ID One Tap button for Connect initialized successfully');
      } catch (error) {
        console.error('VK ID init error:', error);
        setError('Ошибка инициализации VK ID SDK');
      }
    }
    return () => {
      setIsVKIDReady(false);
      try {
        oneTapRef.current?.close?.();
      } catch (error) {
        console.warn('VK ID cleanup error:', error);
      }
      oneTapRef.current = null;
    };
  }, [isVKIDLoaded, appId, isProcessingCallback]);

  const handleVKIDConnectSuccess = useCallback(async (payload: any) => {
    // В режиме mode: Redirect SDK выполняет location.assign(url) и сразу резолвит
    // Promise.resolve() => sendSuccessLoginEvent(undefined) выстреливает ДО навигации.
    // Такой "пустой" LOGIN_SUCCESS не является реальной авторизацией — игнорируем.
    const parsedPayload = parseSilentPayload(payload || {});
    const token = parsedPayload.token;
    const uuid = parsedPayload.uuid;
    const code = parsedPayload.code;
    const deviceId = parsedPayload.deviceId;
    const authType = parsedPayload.type;
    const payloadState = parsedPayload.state;

    const hasAuthData = Boolean(token) || Boolean(code && deviceId);
    if (!hasAuthData && !payloadState) {
      console.log('⏭️ [VK CONNECT] Empty LOGIN_SUCCESS payload (redirect-mode pre-navigation), ignoring');
      return;
    }

    if (successProcessedRef.current) {
      console.log('⏭️ [VK CONNECT] Success already processed, skipping duplicate call');
      return;
    }
    successProcessedRef.current = true;
    try {
      setLoading(true);
      setError(null);
      console.log('VK ID connect success payload:', payload);

      // Проверяем state для защиты от CSRF.
      // Если saved state уже отсутствует — это повторный вызов после успешной попытки, тихо выходим.
      const savedState = storageGet('vkid_connect_state') || sessionGet('vkid_connect_state');
      if (!savedState) {
        authLogger.warning('[VK CONNECT] Нет сохранённого state — пропускаем (storage не пережил редирект?)');
        return;
      }
      const normalizedSavedState = normalizeState(savedState);
      const normalizedPayloadState = normalizeState(payloadState);
      if (!normalizedPayloadState || normalizedPayloadState !== normalizedSavedState) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }
      
      if (token || (code && deviceId)) {
        // Отправляем данные на бэкенд для получения connectionToken
        const result = await execute({
          url: `${API_BASE}/oauth/vk/connect`,
          method: 'POST',
          headers: {
            ...(sessionToken ? { 'ssmm-token': sessionToken } : {}),
            ...getServiceHeaders(),
          },
          data: {
            silentToken: token,
            uuid,
            code,
            deviceId,
            type: authType,
          },
        });

        if (result?.connectionToken) {
          try {
            const payloadData = JSON.parse(atob(result.connectionToken.split('.')[1]));
            const connectionData: ConnectionTokenData = {
              provider: payloadData.provider || 'vk',
              unique: payloadData.unique,
              name: payloadData.name,
              photo: payloadData.photo,
              user: payloadData.user
            };
            
            // Очищаем временные данные
            cleanupTempStorage();
            
            onSuccess(result.connectionToken, connectionData);
          } catch (decodeError) {
            console.error('Ошибка декодирования токена:', decodeError);
            setError('Ошибка обработки данных подключения');
          }
        } else {
          throw new Error('Не получен токен подключения');
        }
      } else {
        throw new Error('No auth payload received from VK ID');
      }
    } catch (error: any) {
      console.error('VK connect error:', error);
      const errorMessage = error?.response?.data?.message || error.message || 'VK connect failed';
      setError(errorMessage);

      cleanupTempStorage();

      // Разрешаем повторную попытку после ошибки
      successProcessedRef.current = false;
    } finally {
      setLoading(false);
      setIsProcessingCallback(false);
    }
  }, [setLoading, setError, execute, sessionToken, onSuccess, cleanupTempStorage]);

  const handleVKIDError = (error: any) => {
    console.error('VK ID Widget Error:', error);
    setError('Ошибка виджета VK ID');
    
    cleanupTempStorage();
  };

  const handleCancel = () => {
    cleanupTempStorage();
    onCancel();
  };

  // Обработка callback от VK ID
  const handleVKIDCallback = useCallback(() => {
    if (callbackProcessedRef.current) {
      return false;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state');
    const payload = urlParams.get('payload');
    const code = urlParams.get('code');
    const deviceId = urlParams.get('device_id');
    const type = urlParams.get('type');

    if (state && code && deviceId) {
      // Проверяем, что это connect mode
      try {
        const normalizedState = normalizeState(state);
        if (!normalizedState) return false;
        const padLen = (4 - (normalizedState.length % 4)) % 4;
        const stateData = JSON.parse(atob(`${normalizedState}${'='.repeat(padLen)}`));
        if (stateData.mode !== 'connect') {
          return false;
        }
      } catch {
        return false;
      }

      callbackProcessedRef.current = true;
      console.log('VK ID connect code_v2 callback detected, processing...');
      setIsProcessingCallback(true);

      handleVKIDConnectSuccess({
        code,
        device_id: deviceId,
        type: type || 'code_v2',
        state,
      });

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('code');
      currentUrl.searchParams.delete('device_id');
      currentUrl.searchParams.delete('type');
      currentUrl.searchParams.delete('state');
      window.history.replaceState({}, document.title, currentUrl.toString());
      return true;
    }
    
    if (payload && state) {
      // Проверяем, что это connect mode
      try {
        const stateData = JSON.parse(atob(state));
        if (stateData.mode !== 'connect') {
          return false;
        }
      } catch {
        return false;
      }

      callbackProcessedRef.current = true;
      console.log('VK ID connect callback detected, processing...');
      setIsProcessingCallback(true);
      
      try {
        let parsedPayload: any;
        try {
          parsedPayload = JSON.parse(payload);
        } catch {
          parsedPayload = JSON.parse(decodeURIComponent(payload));
        }
        
        if (parsedPayload.type === 'silent_token' && parsedPayload.token) {
          handleVKIDConnectSuccess({
            token: parsedPayload.token,
            uuid: parsedPayload.uuid,
            state: state
          });
          
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete('payload');
          currentUrl.searchParams.delete('state');
          window.history.replaceState({}, document.title, currentUrl.toString());
          return true;
        }
      } catch (error) {
        console.error('Error parsing VK ID payload:', error);
        setError('Ошибка обработки ответа VK ID');
        setIsProcessingCallback(false);
      }
    }
    return false;
  }, [handleVKIDConnectSuccess, setError]);

  useEffect(() => {
    handleVKIDCallback();
  }, [handleVKIDCallback]);

  return (
    <div className="flex flex-col min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <button
          onClick={handleCancel}
          disabled={isProcessingCallback}
          className="flex items-center mb-6 transition-colors text-zinc-500 hover:text-zinc-300 disabled:opacity-50"
        >
          <svg className="mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Назад</span>
        </button>
        
        <div className="flex items-center mb-2 space-x-3">
          <div className="overflow-hidden w-10 h-10 rounded-xl">
            <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
              <path d="M11.5 24h1c5.44 0 8.15 0 9.83-1.68C24 20.64 24 17.92 24 12.5v-1.02c0-5.4 0-8.12-1.67-9.8C20.65 0 17.93 0 12.5 0h-1C6.06 0 3.35 0 1.67 1.68 0 3.36 0 6.08 0 11.5v1.02c0 5.4 0 8.12 1.68 9.8C3.36 24 6.08 24 11.5 24Z" fill="#07F"/>
              <path d="M12.77 17.29c-5.47 0-8.59-3.75-8.72-9.99h2.74c.09 4.58 2.11 6.52 3.71 6.92V7.3h2.58v3.95c1.58-.17 3.24-1.97 3.8-3.95h2.58a7.62 7.62 0 0 1-3.51 4.98 7.9 7.9 0 0 1 4.11 5.01h-2.84a4.94 4.94 0 0 0-4.14-3.57v3.57h-.31Z" fill="#fff"/>
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Подключить VK</h1>
            <p className="text-xs text-zinc-500">Связать аккаунт</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {/* Mobile warning */}
        {isMobileDevice() && (
          <div className="p-3 mb-4 rounded-xl border bg-blue-500/10 border-blue-500/30">
            <p className="text-xs text-blue-400">
              💡 Используйте основной браузер для лучшей работы
            </p>
          </div>
        )}

        {/* VK ID Button Container */}
        <div className="p-4 rounded-xl border bg-zinc-900 border-zinc-800">
          <div 
            ref={containerRef}
            id="VkIdSdkOneTapConnect"
            className="flex justify-center items-center min-h-[44px] rounded-xl overflow-hidden"
          />
          
          {isProcessingCallback ? (
            <div className="py-6 text-center">
              <div className="inline-block w-6 h-6 rounded-full border-2 border-zinc-700 border-t-[#0077FF] animate-spin"></div>
              <p className="mt-3 text-sm text-zinc-500">Обработка подключения...</p>
            </div>
          ) : !isVKIDReady && (
            <div className="py-6 text-center">
              <div className="inline-block w-6 h-6 rounded-full border-2 border-zinc-700 border-t-[#0077FF] animate-spin"></div>
              <p className="mt-3 text-sm text-zinc-500">
                {isVKIDLoaded ? 'Инициализация...' : 'Загрузка...'}
              </p>
            </div>
          )}
        </div>

        {/* Help text */}
        <p className="mt-4 text-xs text-zinc-500 text-center">
          Авторизуйтесь через VK для подключения аккаунта
        </p>

        {/* No App ID warning */}
        {!appId && (
          <div className="p-4 mt-4 rounded-xl border bg-amber-500/10 border-amber-500/30">
            <p className="text-sm font-medium text-amber-400">Ошибка конфигурации</p>
            <p className="mt-1 text-xs text-amber-400/70">Не указан VK App ID</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VKConnect;
